import { useCallback, useMemo, useState } from 'react';

import { capitalizeFirstCharLowerOthers } from '@utils/formatting-utils';
import { getGenericLine, getLineCategory } from '@utils/helper-utils';

import {
  useGetAmendmentGroupMedicalPremiumDetailsQuery,
  useGetAmendmentPremiumDetailsQuery,
  useGetAmendmentPremiumDetailsTravelQuery
} from '../amendment.slice';
import {
  GetAmendmentGroupMedicalPremiumDetailsVariables,
  GetAmendmentPremiumDetailsTravelVariables,
  GetAmendmentPremiumDetailsVariables
} from '../types';

import { GenericLines, LineCategory } from 'src/enums';

export function useGetAmendmentPremiumDetails(
  lineCode: string,
  amendmentId: string,
  defaultSorting: {
    pageNumber: number;
    searchKey: string;
    pageSize: number;
    order: 'asc' | 'desc';
    orderBy: string;
  },
  amendmentSubTypeCode?: string
) {
  const lineCategory = getLineCategory(lineCode);
  const isMedical = lineCategory === LineCategory.Medical;
  const isTravel = lineCategory === LineCategory.Travel;
  const isGroupMedical = getGenericLine(lineCode) === GenericLines.MEDICAL_GROUP;
  const isCertificateAmendment =
    isGroupMedical && amendmentSubTypeCode === 'EC11';
  const useMedicalPremium = isMedical || isCertificateAmendment;
  const useGroupMedicalPremium = isGroupMedical && !isCertificateAmendment;

  const [premiumDetailsKey, setPremiumDetailsKey] = useState<number>(0);

  const [getAmendmentPremiumDetailsData] = useGetAmendmentPremiumDetailsQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: !useMedicalPremium
  });

  const [getAmendmentPremiumDetailsTravelData] =
    useGetAmendmentPremiumDetailsTravelQuery({
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      skip: !isTravel
    });
  const [getAmendmentGroupMedicalPremiumDetailsData] =
    useGetAmendmentGroupMedicalPremiumDetailsQuery({
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      skip: !useGroupMedicalPremium
    });

  const handlers = useMemo(
    () => ({
      medical: {
        enabled: useMedicalPremium,
        fetcher: getAmendmentPremiumDetailsData as (options: {
          variables: any;
        }) => Promise<any>,
        buildVariables: (
          baseVars: any
        ): GetAmendmentPremiumDetailsVariables => ({
          amendmentId,
          ...baseVars
        }),
        extractResponse: (r: any) =>
          r?.data?.production
            ?.getAmendmentInsuredPolicyQuoteMedicalGroupByPolicyPerson
      },
      groupMedical: {
        enabled: useGroupMedicalPremium,
        fetcher: getAmendmentGroupMedicalPremiumDetailsData as (options: {
          variables: any;
        }) => Promise<any>,
        buildVariables: (
          baseVars: any
        ): GetAmendmentGroupMedicalPremiumDetailsVariables => ({
          amendmentId,
          ...baseVars
        }),
        extractResponse: (r: any) =>
          r?.data?.production?.getAmendmentGroupPolicyInsuredPolicyQuoteMedical
      },
      travel: {
        enabled: isTravel,
        fetcher: getAmendmentPremiumDetailsTravelData as (options: {
          variables: any;
        }) => Promise<any>,
        buildVariables: (
          baseVars: any
        ): GetAmendmentPremiumDetailsTravelVariables => ({
          amendmentId,
          ...baseVars
        }),
        extractResponse: (r: any) =>
          r?.data?.production
            ?.getAmendmentInsuredPolicyQuoteTravelGroupByPolicyPerson
      }
    }),
    [
      useMedicalPremium,
      isTravel,
      useGroupMedicalPremium,
      amendmentId,
      getAmendmentPremiumDetailsData,
      getAmendmentPremiumDetailsTravelData,
      getAmendmentGroupMedicalPremiumDetailsData
    ]
  );

  const mapItems = useCallback((items: any[]) => {
    return (
      items?.map((item: any) => ({
        ...item,
        insured: useGroupMedicalPremium
          ? item.insured
          : `${item.policyPerson?.person?.code ?? ''} - ${
              item.policyPerson?.person?.fullName ?? ''
            }`,
        state: item.policyPerson?.isRemoved
          ? 'Early Leaver'
          : item.policyPerson?.isNew
          ? 'New Joiner'
          : '',
        relation: capitalizeFirstCharLowerOthers(
          item.policyPerson?.relation ?? ''
        )
      })) ?? []
    );
  }, [useGroupMedicalPremium]);

  const fetchData = useCallback(
    async (
      pageNumber = defaultSorting.pageNumber,
      keyword = defaultSorting.searchKey,
      pageSize = defaultSorting.pageSize,
      order: 'asc' | 'desc' = defaultSorting.order,
      orderBy: string = defaultSorting.orderBy
    ) => {
      const baseVariables = {
        keyword,
        pagination: { pageNumber, pageSize },
        orderBy: {
          attribute: orderBy || defaultSorting.orderBy,
          descending: order === 'desc'
        }
      };

      const handler = Object.values(handlers).find((h) => h.enabled);

      if (!handler)
        return {
          pagedItems: [],
          pageNumber,
          pageSize,
          totalCount: 0
        };

      const response = await handler.fetcher({
        variables: handler.buildVariables(baseVariables)
      });

      const data = handler.extractResponse(response);
      const rawItems = data?.items ?? [];
      const items = mapItems(rawItems);

      const paging = {
        pageNumber: data?.paging?.pageNumber ?? pageNumber,
        pageSize: data?.paging?.pageSize ?? pageSize,
        totalCount: data?.paging?.totalCount ?? items.length
      };

      return {
        pagedItems: items,
        ...paging
      };
    },
    [handlers, defaultSorting, mapItems]
  );

  return { fetchData, premiumDetailsKey, setPremiumDetailsKey };
}
